import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Clock,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  Package,
} from "lucide-react";
import { getToken, base_url } from "../../config/config";

/**
 * ProfitLossDashboard Component
 *
 * This example:
 * - Loads a potentially large API response.
 * - Shows a loading spinner until the *entire* response is fetched.
 * - Renders all data at once after loading.
 */
const ProfitLossDashboard2 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true); // <-- Start in "loading" state
  const [error, setError] = useState(null);
  const [activeProject, setActiveProject] = useState(null);

  // Data fetching function
  async function fetchData() {
    setLoading(true); // <-- Show spinner during fetch
    setError(null);

    try {
      const response = await fetch(`${base_url}/v1/calculateProfit`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const apiData = await response.json();
      console.log("Fetched API Data:", apiData); // Check console for large response
      setData(apiData);

      // Flatten "resolved" data if needed
      const projectsArray =
        Array.isArray(apiData) &&
        apiData.length > 0 &&
        apiData[0].resolved &&
        Array.isArray(apiData[0].resolved)
          ? apiData[0].resolved.flat() // combine multiple inner arrays
          : [];

      // If any project exists, set the first one as active by default
      if (projectsArray.length > 0) {
        setActiveProject(projectsArray[0]);
      }

      setLoading(false); // <-- Done loading
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to fetch data");
      setLoading(false);
    }
  }

  // Load data once when component mounts
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * 1. If still loading => show spinner or loading text.
   * 2. If there's an error => show error screen.
   * 3. Otherwise => show the full dashboard.
   */
  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen bg-gray-50'>
        {/* You can replace this with a spinner, skeleton, etc. */}
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto'></div>
          <p className='mt-4 text-gray-700'>Loading full data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-screen bg-gray-50'>
        <div className='text-center'>
          <AlertCircle size={48} className='mx-auto text-red-500' />
          <p className='mt-4 text-red-700'>{error}</p>
          <button
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            onClick={fetchData}>
            <RefreshCw className='inline-block mr-2 h-4 w-4' />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // At this point: loading is done, data is fetched, no errors.
  // Flatten or extract your projects array from `data`
  const projectsArray =
    Array.isArray(data) &&
    data.length > 0 &&
    data[0].resolved &&
    Array.isArray(data[0].resolved)
      ? data[0].resolved.flat()
      : [];

  if (projectsArray.length === 0) {
    return (
      <div className='flex items-center justify-center h-screen bg-gray-50'>
        <div className='text-center'>
          <p className='mt-4 text-gray-700'>No project data available.</p>
          <button
            className='mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
            onClick={fetchData}>
            <RefreshCw className='inline-block mr-2 h-4 w-4' />
            Refresh Data
          </button>
        </div>
      </div>
    );
  }

  // Prepare summary data
  const profitableProjects = projectsArray.filter((p) => p.isProfit);
  const lossProjects = projectsArray.filter((p) => !p.isProfit);
  const totalProfit = profitableProjects.reduce(
    (sum, p) => sum + p.profitOrLoss,
    0,
  );
  const totalLoss = lossProjects.reduce(
    (sum, p) => sum + Math.abs(p.profitOrLoss),
    0,
  );
  const netProfitLoss = totalProfit - totalLoss;

  // Prepare chart data
  const chartData = projectsArray.map((project) => ({
    name: project._id || "Unknown",
    "Labor Cost": parseFloat(project.laborCost) || 0,
    "Material Cost": parseFloat(project.totalMaterialCost) || 0,
    "Total Revenue": parseFloat(project.Gesamtpreis) || 0,
    "Profit/Loss": parseFloat(project.profitOrLoss) || 0,
  }));

  const pieData = [
    { name: "Profitable", value: profitableProjects.length, color: "#10B981" },
    { name: "Loss", value: lossProjects.length, color: "#EF4444" },
  ];

  /**
   * Render the actual dashboard now that we have all data loaded
   */
  return (
    <div className='bg-gray-50 min-h-screen'>
      {/* Header */}
      <div className='bg-white shadow'>
        <div className='max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 flex justify-between items-center'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>
              Project Profit/Loss Dashboard
            </h1>
            <p className='mt-1 text-sm text-gray-500'>
              All data is fully loaded—analyze your projects now!
            </p>
          </div>
          <button
            onClick={fetchData}
            className='flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition'>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className='max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8'>
        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6'>
          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center'>
              <div className='bg-blue-100 p-3 rounded-full'>
                <Package className='h-6 w-6 text-blue-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-500'>
                  Total Projects
                </p>
                <p className='text-2xl font-semibold text-gray-900'>
                  {projectsArray.length}
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center'>
              <div className='bg-green-100 p-3 rounded-full'>
                <DollarSign className='h-6 w-6 text-green-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-500'>Profitable</p>
                <p className='text-2xl font-semibold text-green-600'>
                  {profitableProjects.length}{" "}
                  <span className='text-sm text-gray-500'>
                    ({totalProfit.toFixed(2)}€)
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center'>
              <div
                className={`p-3 rounded-full ${
                  netProfitLoss >= 0 ? "bg-green-100" : "bg-red-100"
                }`}>
                <DollarSign
                  className={`h-6 w-6 ${
                    netProfitLoss >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-500'>
                  Net Profit/Loss
                </p>
                <p
                  className={`text-2xl font-semibold ${
                    netProfitLoss >= 0 ? "text-green-600" : "text-red-600"
                  }`}>
                  {netProfitLoss >= 0 ? "+" : ""}
                  {netProfitLoss.toFixed(2)}€
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center'>
              <div className='bg-red-100 p-3 rounded-full'>
                <AlertTriangle className='h-6 w-6 text-red-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-500'>
                  Loss Projects
                </p>
                <p className='text-2xl font-semibold text-red-600'>
                  {lossProjects.length}{" "}
                  <span className='text-sm text-gray-500'>
                    ({totalLoss.toFixed(2)}€)
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-lg shadow p-6'>
            <div className='flex items-center'>
              <div className='bg-purple-100 p-3 rounded-full'>
                <Clock className='h-6 w-6 text-purple-600' />
              </div>
              <div className='ml-4'>
                <p className='text-sm font-medium text-gray-500'>Total Hours</p>
                <p className='text-2xl font-semibold text-gray-900'>
                  {projectsArray
                    .reduce(
                      (sum, p) => sum + (parseFloat(p.totalDuration) || 0),
                      0,
                    )
                    .toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6'>
          <div className='bg-white rounded-lg shadow p-6 lg:col-span-2'>
            <h2 className='text-lg font-semibold mb-4'>
              Project Cost & Revenue
            </h2>
            <div className='h-72'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value.toFixed(2)}€`} />
                  <Legend />
                  <Bar dataKey='Labor Cost' fill='#4F46E5' />
                  <Bar dataKey='Material Cost' fill='#7C3AED' />
                  <Bar dataKey='Total Revenue' fill='#10B981' />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className='bg-white rounded-lg shadow p-6'>
            <h2 className='text-lg font-semibold mb-4'>
              Profit vs. Loss Distribution
            </h2>
            <div className='h-72 flex items-center justify-center'>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx='50%'
                    cy='50%'
                    labelLine={false}
                    outerRadius={80}
                    fill='#8884d8'
                    dataKey='value'
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }>
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => value} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Projects and Details */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Project List */}
          <div className='bg-white rounded-lg shadow p-6'>
            <h2 className='text-lg font-semibold mb-4'>Projects</h2>
            <div className='space-y-4 max-h-96 overflow-y-auto'>
              {projectsArray.map((project) => (
                <div
                  key={project._id}
                  className={`p-4 rounded-lg cursor-pointer transition ${
                    activeProject && activeProject._id === project._id
                      ? "bg-blue-50 border border-blue-200"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveProject(project)}>
                  <div className='flex justify-between items-center'>
                    <h3 className='font-medium'>{project._id}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        project.isProfit
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                      {project.isProfit ? "Profit" : "Loss"}
                    </span>
                  </div>
                  <div className='mt-2 text-sm text-gray-500'>
                    <p>Year: {project.projectYear}</p>
                    <p>
                      Total Cost:{" "}
                      {(parseFloat(project.totalProjectCost) || 0).toFixed(2)}€
                    </p>
                    <p>
                      Revenue:{" "}
                      {(parseFloat(project.Gesamtpreis) || 0).toFixed(2)}€
                    </p>
                    <p
                      className={
                        project.isProfit
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }>
                      {project.isProfit ? "Profit: +" : "Loss: "}
                      {Math.abs(parseFloat(project.profitOrLoss) || 0).toFixed(
                        2,
                      )}
                      €
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Details */}
          <div className='bg-white rounded-lg shadow p-6 lg:col-span-2'>
            {activeProject ? (
              <>
                <div className='flex justify-between items-center mb-4'>
                  <h2 className='text-lg font-semibold'>
                    Project Details: {activeProject._id}
                  </h2>
                  <span
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      activeProject.isProfit
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                    {activeProject.isProfit
                      ? `Profit: +${(
                          parseFloat(activeProject.profitOrLoss) || 0
                        ).toFixed(2)}€`
                      : `Loss: ${(
                          parseFloat(activeProject.profitOrLoss) || 0
                        ).toFixed(2)}€`}
                  </span>
                </div>

                {/* Financial Metrics */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
                  <div className='bg-gray-50 p-4 rounded-lg'>
                    <p className='text-sm text-gray-500'>Labor Cost</p>
                    <p className='text-xl font-semibold text-gray-900'>
                      {(parseFloat(activeProject.laborCost) || 0).toFixed(2)}€
                    </p>
                    <p className='text-xs text-gray-500'>
                      {(parseFloat(activeProject.totalDuration) || 0).toFixed(
                        2,
                      )}{" "}
                      hours
                    </p>
                  </div>
                  <div className='bg-gray-50 p-4 rounded-lg'>
                    <p className='text-sm text-gray-500'>Material Cost</p>
                    <p className='text-xl font-semibold text-gray-900'>
                      {(
                        parseFloat(activeProject.totalMaterialCost) || 0
                      ).toFixed(2)}
                      €
                    </p>
                    <p className='text-xs text-gray-500'>
                      {activeProject.invoiceIds
                        ? activeProject.invoiceIds.length
                        : 0}{" "}
                      invoices
                    </p>
                  </div>
                  <div className='bg-gray-50 p-4 rounded-lg'>
                    <p className='text-sm text-gray-500'>Total Revenue</p>
                    <p className='text-xl font-semibold text-gray-900'>
                      {(parseFloat(activeProject.Gesamtpreis) || 0).toFixed(2)}€
                    </p>
                    <p className='text-xs text-gray-500'>
                      Project Year: {activeProject.projectYear || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Work Records */}
                <h3 className='font-medium text-gray-700 mb-3'>Work Records</h3>
                <div className='bg-gray-50 rounded-lg overflow-hidden'>
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-100'>
                      <tr>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Date
                        </th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          User
                        </th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Work Type
                        </th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Hours
                        </th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      {activeProject.records &&
                      activeProject.records.length > 0 ? (
                        activeProject.records.map((record) => (
                          <tr key={record._id} className='hover:bg-gray-50'>
                            <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                              {record.date}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                              {record.user}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                              {record.Arbeiten}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                              {record.TotalTime}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                              {record.StartTime} - {record.EndTime}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan='5'
                            className='px-6 py-4 text-center text-sm text-gray-500'>
                            No records available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Work Description */}
                <div className='mt-6'>
                  <h3 className='font-medium text-gray-700 mb-3'>
                    Work Description
                  </h3>
                  <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
                    {activeProject.records &&
                    activeProject.records.length > 0 ? (
                      activeProject.records.map((record) => (
                        <div
                          key={`desc-${record._id}`}
                          className='text-sm text-gray-700'>
                          <p className='font-medium'>
                            {record.date} by {record.user}:
                          </p>
                          <p className='mt-1'>{record.description}</p>
                        </div>
                      ))
                    ) : (
                      <p className='text-sm text-gray-500'>
                        No work descriptions available
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className='flex flex-col items-center justify-center h-full py-12'>
                <AlertCircle size={48} className='text-gray-400' />
                <p className='mt-4 text-gray-500'>
                  Select a project to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossDashboard2;
